import { createRouter, createWebHistory, type RouteRecordRaw } from 'vue-router';
import { useAuthStore } from '../stores/auth';
import { OAUTH_CALLBACK_PATH, setReauthHandler } from '../api/http';

const routes: RouteRecordRaw[] = [
  {
    path: '/',
    component: () => import('../pages/DashboardPage.vue'),
    meta: { title: '今日总览' },
  },
  {
    path: '/bind',
    component: () => import('../pages/BindingPage.vue'),
    meta: { title: '绑定中心' },
  },
  {
    path: '/invite',
    component: () => import('../pages/InvitePage.vue'),
    meta: { title: '邀请填表' },
  },
  {
    path: '/drafts',
    component: () => import('../pages/DraftInboxPage.vue'),
    meta: { title: '可用任务' },
  },
  // 任务列表 — 按状态筛选、按订单尾号 / 昵称 / 角色 / 任务号搜索
  {
    path: '/tasks',
    component: () => import('../pages/TaskListPage.vue'),
    meta: { title: '任务' },
  },
  // 手机任务页 — 企微"可以开工" / "内容待确认"卡片的链接目标: 切状态、确认内容、看上号信息
  {
    path: '/tasks/:id',
    component: () => import('../pages/StaffTaskPage.vue'),
    meta: { title: '任务', layout: 'blank' },
  },
  {
    path: '/push',
    component: () => import('../pages/PushPage.vue'),
    meta: { title: '今日总览图' },
  },
  {
    path: '/dispatch',
    component: () => import('../pages/DispatchPage.vue'),
    meta: { title: '接客员调度' },
  },
  {
    path: '/products',
    component: () => import('../pages/ProductsPage.vue'),
    meta: { title: '商品管理' },
  },
  // 闲鱼链接 — 闲管家同步 + 链接 ↔ 商品映射
  {
    path: '/xianyu-listings',
    component: () => import('../pages/XianyuListingsPage.vue'),
    meta: { title: '闲鱼链接' },
  },
  {
    path: '/products/new',
    component: () => import('../pages/ProductFormPage.vue'),
    meta: { title: '新建商品' },
  },
  {
    path: '/products/:id/edit',
    component: () => import('../pages/ProductFormPage.vue'),
    meta: { title: '编辑商品' },
  },
  // 营销中台 — 价格策略
  {
    path: '/price-plans',
    component: () => import('../pages/PricePlansPage.vue'),
    meta: { title: '价格策略' },
  },
  {
    path: '/price-plans/new',
    component: () => import('../pages/PricePlanFormPage.vue'),
    meta: { title: '新建价格策略' },
  },
  {
    path: '/price-plans/:id/edit',
    component: () => import('../pages/PricePlanFormPage.vue'),
    meta: { title: '编辑价格策略' },
  },
  // 营销中台 — 优惠券
  {
    path: '/coupons',
    component: () => import('../pages/CouponsPage.vue'),
    meta: { title: '优惠券' },
  },
  {
    path: '/coupons/new',
    component: () => import('../pages/CouponFormPage.vue'),
    meta: { title: '新建优惠券' },
  },
  {
    path: '/coupons/:id/edit',
    component: () => import('../pages/CouponFormPage.vue'),
    meta: { title: '编辑优惠券' },
  },
  // 营销中台 — 会员等级
  {
    path: '/customer-tiers',
    component: () => import('../pages/CustomerTiersPage.vue'),
    meta: { title: '会员等级' },
  },
  {
    path: '/customer-tiers/new',
    component: () => import('../pages/CustomerTierFormPage.vue'),
    meta: { title: '新建会员等级' },
  },
  {
    path: '/customer-tiers/:id/edit',
    component: () => import('../pages/CustomerTierFormPage.vue'),
    meta: { title: '编辑会员等级' },
  },
  // 钱包 — 充值档位
  {
    path: '/recharge-plans',
    component: () => import('../pages/RechargePlansPage.vue'),
    meta: { title: '充值档位' },
  },
  {
    path: '/recharge-plans/new',
    component: () => import('../pages/RechargePlanFormPage.vue'),
    meta: { title: '新建充值档位' },
  },
  {
    path: '/recharge-plans/:id/edit',
    component: () => import('../pages/RechargePlanFormPage.vue'),
    meta: { title: '编辑充值档位' },
  },
  // 会员 — 小程序用户总览
  {
    path: '/mini-program-users',
    component: () => import('../pages/MiniProgramUsersPage.vue'),
    meta: { title: '小程序用户' },
  },
  // 营销中台 — 活动横幅
  {
    path: '/banners',
    component: () => import('../pages/BannersPage.vue'),
    meta: { title: '活动横幅' },
  },
  {
    path: '/banners/new',
    component: () => import('../pages/BannerFormPage.vue'),
    meta: { title: '新建活动横幅' },
  },
  {
    path: '/banners/:id/edit',
    component: () => import('../pages/BannerFormPage.vue'),
    meta: { title: '编辑活动横幅' },
  },
  // 营销中台 — 季节活动 (小程序艾欧泽亚日历与活动详情)
  {
    path: '/season-events',
    component: () => import('../pages/SeasonEventsPage.vue'),
    meta: { title: '季节活动' },
  },
  {
    path: '/season-events/new',
    component: () => import('../pages/SeasonEventFormPage.vue'),
    meta: { title: '新建季节活动' },
  },
  {
    path: '/season-events/:id/edit',
    component: () => import('../pages/SeasonEventFormPage.vue'),
    meta: { title: '编辑季节活动' },
  },
  // 日常接待 — 客户反馈 (App 提交)
  {
    path: '/customer-feedback',
    component: () => import('../pages/CustomerFeedbackPage.vue'),
    meta: { title: '客户反馈' },
  },
  // 系统设置 — 客户端错误 (App 上报)
  {
    path: '/client-errors',
    component: () => import('../pages/ClientErrorsPage.vue'),
    meta: { title: '客户端错误' },
  },
  // 营销中台 — 图片审核 (没法机审的相册照片与幻化图片)
  {
    path: '/image-reviews',
    component: () => import('../pages/ImageReviewsPage.vue'),
    meta: { title: '图片审核' },
  },
  // 支付追溯
  {
    path: '/payments-trace',
    component: () => import('../pages/PaymentsTracePage.vue'),
    meta: { title: '支付追溯' },
  },
  // 退款审批
  {
    path: '/refund-requests',
    component: () => import('../pages/RefundRequestsPage.vue'),
    meta: { title: '退款审批' },
  },
  // API 令牌 (脚本调用后台商品接口的凭证)
  {
    path: '/api-tokens',
    component: () => import('../pages/ApiTokensPage.vue'),
    meta: { title: 'API 令牌' },
  },
  // 工作台安装包 (新电脑首次安装工作台用, 带后台登录身份下载)
  {
    path: '/workbench-download',
    component: () => import('../pages/WorkbenchDownloadPage.vue'),
    meta: { title: '工作台安装包' },
  },
  // 金价管理 (singleton 全局设置)
  {
    path: '/gold-price',
    component: () => import('../pages/GoldPricePage.vue'),
    meta: { title: '金价管理' },
  },
  // 客服工具 — 群发商品 + 自定义报价单
  {
    path: '/share-product',
    component: () => import('../pages/ShareProductPage.vue'),
    meta: { title: '群发商品' },
  },
  {
    path: '/custom-quotes',
    component: () => import('../pages/CustomQuotesPage.vue'),
    meta: { title: '自定义报价单' },
  },
  // 企微聊天侧边栏 — 推商品小程序卡片 (无需 Cat 登录, 走企微自有 OAuth 识别员工)
  // public: true → 绕过 router guard 的 Cat 登录校验; layout: 'blank' → 不渲染 AppShell 后台框架
  {
    path: '/wx-push',
    component: () => import('../pages/WxPushPage.vue'),
    meta: { title: '推商品到会话', layout: 'blank', public: true },
  },
  // 会员等级权益 — 定义 + 升级礼履约工作台
  {
    path: '/tier-benefits',
    component: () => import('../pages/TierBenefitsPage.vue'),
    meta: { title: '会员权益' },
  },
  // 旧路径兼容(避免书签 / 现有链接 404)
  { path: '/customers', redirect: '/bind' },
  { path: '/groups', redirect: { path: '/bind', query: { tab: 'groups' } } },
  { path: '/orders/unbound', redirect: '/invite' },
  { path: '/tasks/review', redirect: '/drafts' },
  { path: '/broadcasts', redirect: '/push' },
  { path: '/greeters', redirect: '/dispatch' },
  {
    path: '/auth/oauth2-callback',
    component: () => import('../pages/OAuth2CallbackPage.vue'),
    meta: { title: '正在登录', layout: 'blank' },
  },
];

export const router = createRouter({
  history: createWebHistory(),
  routes,
});

/**
 * 检测当前是否在企业微信内置浏览器 (X5 WeChat WebView)
 * — 非企微浏览器没法走 OAuth2 (企微会拦截显示"请在企业微信客户端打开链接")
 */
function isInWxworkBrowser(): boolean {
  if (typeof navigator === 'undefined') return true;
  const ua = navigator.userAgent.toLowerCase();
  // 企业微信内置 webview 的 UA 都含 wxwork
  return /wxwork/.test(ua);
}

/** sessionStorage flag - "用户已主动切到开发者登录, 别再自动跳 OAuth2" */
const DEV_LOGIN_FLAG = 'cat.dev-login-mode';

router.beforeEach(async (to) => {
  const auth = useAuthStore();
  if (to.meta.title) document.title = `${to.meta.title} | 喵喵小窝`;

  // 0. 公开页 (meta.public) 一律放行 — 不需要 Cat 登录/token。
  //    企微聊天侧边栏 /wx-push 走企微自有 OAuth 识别员工, 与 Cat JWT 鉴权无关。
  if (to.meta.public === true) return true;

  // 1. OAuth2 回调页一律放行 (含 ?code=... 正常流和无 code 的 dev 流)
  if (to.path === '/auth/oauth2-callback') return true;

  // 2. 已登录: 放行
  if (auth.token) return true;

  // 3. URL 带 ?dev=1 → 永久标记 dev 模式, 跳到登录页
  if (to.query.dev !== undefined) {
    sessionStorage.setItem(DEV_LOGIN_FLAG, '1');
  }

  // 4. dev 模式 或 非企微浏览器 → 跳到登录页 (而非 OAuth2 URL)
  if (sessionStorage.getItem(DEV_LOGIN_FLAG) === '1' || !isInWxworkBrowser()) {
    return { path: '/auth/oauth2-callback', query: { return: to.fullPath } };
  }

  // 5. 企微浏览器内未登录: 走正常 OAuth2 流
  auth.beginAuthorize(to.fullPath);
  return false;
});

/**
 * 接口 401 (登录过期) 时的重新登录方式, 与上面守卫的未登录分支一致:
 * 企微内直接走 OAuth2 并回到当前页面; 开发者模式或非企微浏览器去登录页, 登录后回到当前页面。
 */
setReauthHandler((returnTo) => {
  const auth = useAuthStore();
  auth.logout();
  if (sessionStorage.getItem(DEV_LOGIN_FLAG) === '1' || !isInWxworkBrowser()) {
    window.location.replace(`${OAUTH_CALLBACK_PATH}?return=${encodeURIComponent(returnTo)}`);
    return;
  }
  void auth.beginAuthorize(returnTo);
});
